export interface PublicTenantHandoff {
  isMalay: boolean;
  language: 'ms' | 'en' | 'ta';
  source: 'public-route' | 'domain' | 'query';
  landingPath?: string;
  timestamp: number;
}

const PUBLIC_TENANT_HANDOFF_KEY = 'tkm_public_tenant_context';

function getStorage(): Storage | null {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    return window.sessionStorage;
  }
  if (typeof globalThis !== 'undefined' && (globalThis as unknown as { sessionStorage?: Storage }).sessionStorage) {
    return (globalThis as unknown as { sessionStorage: Storage }).sessionStorage;
  }
  return null;
}

/**
 * Stores temporary pre-authentication tenant intent in tab-scoped sessionStorage.
 * This hint ONLY influences pre-auth presentation & loading states.
 * It DOES NOT grant authorization or override authenticated tenant settings.
 */
export function setPublicTenantIntent(intent: Omit<PublicTenantHandoff, 'timestamp'>): void {
  try {
    const storage = getStorage();
    if (!storage) return;
    const payload: PublicTenantHandoff = {
      ...intent,
      timestamp: Date.now(),
    };
    storage.setItem(PUBLIC_TENANT_HANDOFF_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage write errors (e.g. private browsing restriction)
  }
}

/**
 * Retrieves the temporary pre-authentication tenant intent hint from sessionStorage.
 */
export function getPublicTenantIntent(): PublicTenantHandoff | null {
  try {
    const storage = getStorage();
    if (!storage) return null;
    const raw = storage.getItem(PUBLIC_TENANT_HANDOFF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PublicTenantHandoff;
    if (typeof parsed?.isMalay === 'boolean') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Resolves the appropriate Home/Back navigation path based on pre-auth tenant context.
 * Returns '/ms' or custom landingPath for Malay visitors, or '/' for default/Tamil.
 */
export function resolveHomeNavigationPath(): string {
  const intent = getPublicTenantIntent();
  if (intent?.isMalay && intent.landingPath) {
    return intent.landingPath;
  }
  if (intent?.isMalay) {
    return '/ms';
  }
  return '/';
}

/**
 * Resolves tenant-aware logout destination path.
 * Sequence:
 * 1. If authenticated user's school / portal context is explicitly Tamil -> returns '/' (Tamil landing).
 * 2. If authenticated user's school / portal context is explicitly Malay -> returns '/ms' (Malay landing).
 * 3. Fallback to pre-auth sessionStorage intent (if isMalay -> '/ms', else '/').
 */
export function resolveLogoutDestinationPath(options?: {
  schoolLanguage?: { ui_default?: string | null; allowed?: string[] | null } | null;
  uiLanguage?: string | null;
}): string {
  // 1. Check authenticated school language first (authoritative!)
  if (options?.schoolLanguage?.ui_default) {
    const isMalay = options.schoolLanguage.ui_default === 'ms';
    return isMalay ? '/ms' : '/';
  }

  // 2. Check portal UI language if available
  if (options?.uiLanguage) {
    return options.uiLanguage === 'ms' ? '/ms' : '/';
  }

  // 3. Fallback to pre-auth sessionStorage intent
  return resolveHomeNavigationPath();
}

/**
 * Clears the temporary pre-authentication tenant intent hint from sessionStorage.
 * Called on logout or when visiting non-Malay public routes.
 */
export function clearPublicTenantIntent(): void {
  try {
    const storage = getStorage();
    if (!storage) return;
    storage.removeItem(PUBLIC_TENANT_HANDOFF_KEY);
  } catch {
    // Ignore storage errors
  }
}
